import React, { useEffect, useState } from 'react';

import { useRevampedCheckoutFlowVariant } from '@editor/global/growthbookUtils';
import analytics from '@repo/analytics';

import { UPGRADE_MODAL_CLICK } from '../../global/analytics/events';
import {
  planSelectionStoreSelector,
  usePlanSelectionStore,
} from '../../stores/planSelectionStore';
import { useUserStore, userStoreSelector } from '../../stores/userStore';
import { SubscriptionModalContent } from '../Subscription';

import { getConfigByType } from './config';
import { UpgradeModalContentProps } from './types';
import UpgradeModalIncentiveContent from './UpgradeModalIncentiveContent';

/**
 * Content for the upgrade flow modal.
 */
const UpgradeFlowModalContent: React.FC<UpgradeModalContentProps> = ({
  onSuccess,
  onClose,
  returnUrlParams,
  returnUrlFailed,
  resetReturnUrlFailed,
  isOnlyExpertUpgrade,
  ...contentProps
}) => {
  const showRevampedCheckoutFlow = useRevampedCheckoutFlowVariant() !== 'off';

  const [flowState, setFlowState] = useState<'incentive' | 'subscription'>(
    () =>
      returnUrlFailed || contentProps.contentType === 'noIncentiveUpgrade'
        ? 'subscription'
        : 'incentive'
  );

  const user = useUserStore(userStoreSelector.user);
  const showOnlyOnePlan = getConfigByType(
    contentProps.contentType
  ).showOnlyOnePlan;

  const { setUpgrade, setUpgradeToExpertPlan } = usePlanSelectionStore(
    planSelectionStoreSelector.actions
  );

  const type = contentProps.contentType;

  useEffect(() => {
    if (type === 'freeTrial' && showRevampedCheckoutFlow) {
      setFlowState('subscription');
    }
  }, [type, showRevampedCheckoutFlow]);

  const handleUpgrade = (): void => {
    if (contentProps.onClickCallback) {
      contentProps.onClickCallback();
    } else {
      // track with new event
      void analytics.track(UPGRADE_MODAL_CLICK, { label: type });

      setFlowState('subscription');
    }
  };

  useEffect(() => {
    // If contentType is noIncentiveUpgrade, display the next higher plan by default on the subscription page.
    if (contentProps.contentType === 'noIncentiveUpgrade') {
      if (isOnlyExpertUpgrade) {
        setUpgradeToExpertPlan(true);
      } else {
        setUpgrade(true);
      }
    }
  }, [
    isOnlyExpertUpgrade,
    setUpgrade,
    setUpgradeToExpertPlan,
    contentProps.contentType,
  ]);

  return flowState === 'incentive' ? (
    <UpgradeModalIncentiveContent
      user={user}
      onSecondaryButtonClick={onClose}
      onUpgrade={handleUpgrade}
      {...contentProps}
    />
  ) : (
    <SubscriptionModalContent
      resetReturnUrlFailed={resetReturnUrlFailed}
      returnUrlFailed={returnUrlFailed}
      returnUrlParams={returnUrlParams}
      shouldShowOnlyOnePlan={showOnlyOnePlan}
      onClose={onClose}
      onSuccess={onSuccess}
    />
  );
};

export default UpgradeFlowModalContent;
